import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { PathStanding, StepStanding, StepUnlock } from '@aurora/core'
import { bookShelf, LEVELS, pictureSrc, unlockPhrase, WORDS, wordFamilies } from '@aurora/core'
import { playStep } from '../../lib/games'
import { play } from '../../lib/feedback'

/**
 * The reading path: four tracks she moves along, drawn as one journey.
 *
 * Every step is a game that ships with the app, so there is nothing to wait
 * for: a tap plays it. Where she is comes from her own memory and what each
 * game saved, in words a parent reads at a glance. A locked step says what
 * opens it rather than hiding, because what is next is half of what a path
 * is for.
 */
export function ReadingPath({ standing, childId }: { standing: PathStanding; childId: string }) {
  const nextId = standing.next?.step.id
  const go = (s: StepStanding) => {
    if (s.state === 'locked') return
    play('tap')
    playStep(childId, s.step.id)
  }

  return (
    <ol className="path">
      {/* A track whose every step the parent took off her path (Word families, say) is not drawn. */}
      {standing.tracks.filter(({ steps }) => steps.length).map(({ track, steps }) => (
        <li key={track.id} className={`path-track is-${trackState(steps)}`}>
          <span className="path-node" aria-hidden="true" />
          <header className="path-track-head">
            <h3 className="path-track-title">{track.title}</h3>
            <p className="path-track-blurb">{track.blurb}</p>
          </header>
          {track.id === 'blend' ? (
            <Blending step={steps[0]!} next={nextId === 'blend'} onPlay={go} />
          ) : track.id === 'families' ? (
            steps[0]!.state === 'locked' ? <LockedFamilies step={steps[0]!} /> : <StepCard step={steps[0]!} next={nextId === 'families'} onPlay={go} />
          ) : track.id === 'sight' || track.id === 'sounds' ? (
            <Sets steps={steps} unit={track.id === 'sounds' ? 'sounds' : 'words'} nextId={nextId} onPlay={go} />
          ) : (
            <Shelf steps={steps} nextId={nextId} onPlay={go} />
          )}
        </li>
      ))}
    </ol>
  )
}

function trackState(steps: StepStanding[]): string {
  if (steps.every((s) => s.state === 'done')) return 'done'
  if (steps.every((s) => s.state === 'locked')) return 'locked'
  return steps.some((s) => s.state === 'playing' || s.state === 'done') ? 'moving' : 'open'
}

/**
 * Blending climbs its own five-rung ladder, drawn from where she stands: a
 * level she holds is a filled dot with a check, the rung the game plays at
 * today is ringed and larger (it can run ahead of what she holds), and the
 * rungs ahead wait as quiet outlines. The level she is working toward
 * wears a ring that fills as she comes to hold its words (and the line into
 * it fills the same), from the same count every step waiting on that level
 * names, so the ladder, the words under it and the locks say one thing.
 */
function Blending({ step, next, onPlay }: { step: StepStanding; next: boolean; onPlay: (s: StepStanding) => void }) {
  return (
    <div className={`path-step path-blend is-${step.state}${next ? ' is-next' : ''}`}>
      <BlendLadder step={step} />
      <StepFoot step={step} next={next} onPlay={onPlay} fallback="Start at the first rung: sounds she can stretch" />
    </div>
  )
}

/** The five rungs as Blending stands for her; the path and the Today hero draw the same ladder. */
export function BlendLadder({ step }: { step: StepStanding }) {
  const held = step.state === 'done' ? LEVELS.length : step.level ?? 0
  const rung = step.rung ?? Math.max(1, held)
  // How far each rung's line in is drawn: full for a level she holds, part way for the one she is nearing.
  const fill = (n: number) => Math.max(0, Math.min(1, step.fraction * LEVELS.length - (n - 1)))
  const toward = step.toward
  return (
    <ol className="path-ladder" aria-label={held ? `Blending: holds level ${held} of ${LEVELS.length}, plays level ${rung}` : `Blending: plays level ${rung} of ${LEVELS.length}`}>
      {LEVELS.map((lv, i) => {
        const n = i + 1
        const state = n === rung && step.state !== 'done' ? 'here' : n <= held ? 'done' : 'ahead'
        const nearing = toward?.level === n
        return (
          <li
            key={n}
            className={`path-rung is-${state}${nearing ? ' is-nearing' : ''}`}
            style={{ '--fill': fill(n), ...(nearing ? { '--toward': toward.held / toward.needed } : {}) } as React.CSSProperties}
            aria-current={state === 'here' ? 'step' : undefined}
            title={nearing ? `${toward.held} of ${toward.needed} level ${n} words read well` : undefined}
          >
            <span className="path-rung-dot" aria-hidden="true">
              {state === 'done' ? <CheckIcon /> : n}
            </span>
            <span className="path-rung-name">{lv.name}</span>
          </li>
        )
      })}
    </ol>
  )
}

function StepCard({ step, next, onPlay }: { step: StepStanding; next: boolean; onPlay: (s: StepStanding) => void }) {
  return (
    <div className={`path-step path-card is-${step.state}${next ? ' is-next' : ''}`}>
      <Cover word={step.step.cover} size={52} />
      <StepFoot step={step} next={next} onPlay={onPlay} fallback={step.step.blurb} title={step.step.title} />
    </div>
  )
}

/** The words, where she is, and the one button; a locked step says what opens it instead. */
function StepFoot({ step, next, onPlay, fallback, title }: {
  step: StepStanding; next: boolean; onPlay: (s: StepStanding) => void; fallback: string; title?: string
}) {
  const locked = step.state === 'locked'
  return (
    <div className="path-foot">
      <div className="path-foot-text">
        {title && <p className="path-step-title">{title}{next && <NextTag />}</p>}
        <p className={`path-progress${locked ? ' is-locked' : ''}`}>
          {locked ? <><LockIcon /> {step.unlockHint}</> : step.progress ?? fallback}
          {!title && next && <NextTag />}
        </p>
      </div>
      {!locked && (
        <button className={`btn ${next ? 'btn-primary' : 'btn-ghost'} btn-sm path-play`} onClick={() => onPlay(step)}>
          <PlayIcon /> {step.state === 'open' ? 'Start' : step.state === 'done' ? 'Again' : 'Play'}
        </button>
      )}
    </div>
  )
}

/**
 * Word families before they open: one slim line in the track, saying what
 * opens them and the families she will meet first, rather than a card the
 * size of a game she cannot play.
 */
function LockedFamilies({ step }: { step: StepStanding }) {
  return (
    <div className="path-locked-line">
      <p className="path-progress is-locked"><LockIcon /> {step.unlockHint}</p>
      <p className="path-fams" aria-label={`First families: ${FIRST_FAMILIES.join(', ')}`}>
        <span className="path-fams-label">First up</span>
        {FIRST_FAMILIES.map((f) => <span key={f} className="path-fam">-{f}</span>)}
      </p>
    </div>
  )
}

/** The families with the easiest words (cat, hat, sat before ship, shop), the ones she meets first. */
const FIRST_FAMILIES = (() => {
  const level = new Map(WORDS.map((w) => [w.word, w.level]))
  const ease = (words: Array<{ word: string }>) => Math.min(...words.map((w) => level.get(w.word) ?? 9))
  return Object.entries(wordFamilies())
    .sort(([, a], [, b]) => ease(a.words) - ease(b.words) || b.words.length - a.words.length)
    .slice(0, 4)
    .map(([key]) => key)
})()

const NextTag = () => <span className="path-next-tag">Up next</span>

/**
 * A track of small sets (sight words, letter sounds), each a little stack
 * of what it teaches. Only the first locked set explains itself; the rest
 * simply wait behind it, so the row reads as a way forward rather than a
 * wall of padlocks.
 */
function Sets({ steps, unit, nextId, onPlay }: { steps: StepStanding[]; unit: 'words' | 'sounds'; nextId?: string; onPlay: (s: StepStanding) => void }) {
  const firstLocked = steps.findIndex((s) => s.state === 'locked')
  const shown = firstLocked < 0 ? steps : steps.slice(0, firstLocked + 1)
  const waiting = firstLocked < 0 ? 0 : steps.length - shown.length
  return (
    <div className="path-sets">
      {shown.map((s) => {
        const words = setItems(s)
        const next = s.step.id === nextId
        return (
          <button
            key={s.step.id}
            className={`path-set is-${s.state}${next ? ' is-next' : ''}`}
            disabled={s.state === 'locked'}
            onClick={() => onPlay(s)}
            aria-label={`${words.join(', ')}. ${s.state === 'locked' ? s.unlockHint : s.progress ?? 'Not started'}`}
          >
            <span className="path-set-words">
              {words.map((w) => <span key={w}>{w}</span>)}
            </span>
            <span className="path-set-bar" style={{ '--fill': s.fraction } as React.CSSProperties} aria-hidden="true" />
            <span className="path-set-line">
              {s.state === 'locked' ? <><LockIcon /> {s.unlockHint}</> : s.state === 'done' ? <><CheckIcon /> Knows them all</> : s.progress ?? 'Not started'}
              {next && s.state !== 'locked' && <NextTag />}
            </span>
          </button>
        )
      })}
      {waiting > 0 && (
        <p className="path-sets-more">
          {waiting} more {waiting === 1 ? 'set' : 'sets'} after that, {steps.reduce((n, s) => n + setItems(s).length, 0)} {unit} in all
        </p>
      )}
    </div>
  )
}

/** What one set teaches, from its own config: its words, or its letters. Sets are not all the same size. */
const setItems = (s: StepStanding): string[] => (s.step.config.words ?? s.step.config.letters ?? []) as string[]

/**
 * Little books on one shelf that scrolls sideways, opened with the book she
 * is on in the middle. The books she can read stand full size; the ones
 * still ahead wait in core's bookShelf stacks (the phone draws the same
 * ones), each titled by its level, with the one thing that opens it. Any
 * book still ahead opens as a page a parent can read: the story as text
 * and what it waits on, so what is coming is never a closed door.
 */
function Shelf({ steps, nextId, onPlay }: { steps: StepStanding[]; nextId?: string; onPlay: (s: StepStanding) => void }) {
  const shelf = useRef<HTMLUListElement>(null)
  const { open, ahead } = bookShelf(steps)
  const [peek, setPeek] = useState<string | null>(null)
  const [edges, setEdges] = useState({ left: false, right: false })
  const peeked = steps.find((s) => s.step.id === peek)
  const measure = useCallback(() => {
    const el = shelf.current
    if (el) setEdges({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 })
  }, [])
  // Centre the book she is on, inside the shelf only: the page stays where it is.
  useLayoutEffect(() => {
    const el = shelf.current
    const here = el?.querySelector<HTMLElement>('.path-book.is-next, .path-book.is-playing')?.parentElement
    if (el && here) el.scrollLeft = here.offsetLeft - el.offsetLeft - (el.clientWidth - here.offsetWidth) / 2
    measure()
  }, [nextId, measure])
  // The shelf's own width decides the arrows: a window resize, a sidebar or a font loading can all change it.
  useEffect(() => {
    const el = shelf.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const watch = new ResizeObserver(measure)
    watch.observe(el)
    return () => watch.disconnect()
  }, [measure])
  const slide = (dir: 1 | -1) => {
    const el = shelf.current
    el?.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
  }
  const toggle = (id: string) => {
    play('tap')
    setPeek((p) => (p === id ? null : id))
  }

  return (
    <div className="path-books">
      <div className={`path-shelf-wrap${edges.left ? ' has-left' : ''}${edges.right ? ' has-right' : ''}`}>
        <ul className="path-shelf" ref={shelf} onScroll={measure}>
          {open.map((s) => {
            const level = Number(s.step.config.level)
            const reads = Math.round(s.fraction * 3)
            const next = s.step.id === nextId
            return (
              <li key={s.step.id}>
                <button
                  className={`path-book is-${s.state}${next ? ' is-next' : ''}`}
                  onClick={() => onPlay(s)}
                  aria-label={`${s.step.title}. Read ${reads} of 3 times`}
                >
                  <span className="path-book-cover">
                    <Cover word={s.step.cover} size={88} />
                    {next && <span className="path-book-next">Up next</span>}
                  </span>
                  <span className="path-book-title">{s.step.title}</span>
                  <span className="path-book-meta">
                    {reads > 0 && (
                      <span className="path-pips" aria-hidden="true">
                        {[0, 1, 2].map((i) => <span key={i} className={`path-pip${i < reads ? ' is-on' : ''}`} />)}
                      </span>
                    )}
                    <span>{s.state === 'done' ? 'Hers now' : reads ? `Read ${reads === 1 ? 'once' : `${reads} times`}` : `New · level ${level}`}</span>
                  </span>
                </button>
              </li>
            )
          })}
          {ahead.map((g) => (
            <li key={g.books[0]!.step.id} className="path-ahead-slot">
              <div className="path-ahead">
                <span className="path-ahead-stack">
                  {g.books.slice(0, 3).map((b, i) => (
                    <button
                      key={b.step.id}
                      className={`path-ahead-cover${peek === b.step.id ? ' is-peeked' : ''}`}
                      style={{ '--i': i } as React.CSSProperties}
                      onClick={() => toggle(b.step.id)}
                      aria-label={`Look inside ${b.step.title}`}
                    >
                      <Cover word={b.step.cover} size={52} />
                    </button>
                  ))}
                </span>
                <span className="path-ahead-title">
                  {g.books.length} level {g.level} {g.books.length === 1 ? 'book' : 'books'}
                </span>
                <span className="path-ahead-books">
                  {g.books.map((b) => (
                    <button key={b.step.id} className={peek === b.step.id ? 'is-peeked' : ''} onClick={() => toggle(b.step.id)} aria-expanded={peek === b.step.id}>
                      {b.step.title}
                    </button>
                  ))}
                </span>
                <span className="path-ahead-hint"><LockIcon /><span><Gate unlock={g.unlock} /></span></span>
              </div>
            </li>
          ))}
        </ul>
        <button className="path-shelf-arrow is-left" onClick={() => slide(-1)} aria-label="Earlier books" tabIndex={edges.left ? 0 : -1}><ArrowIcon /></button>
        <button className="path-shelf-arrow is-right" onClick={() => slide(1)} aria-label="More books" tabIndex={edges.right ? 0 : -1}><ArrowIcon /></button>
      </div>
      {peeked && <BookPeek step={peeked} onClose={() => setPeek(null)} />}
    </div>
  )
}

/**
 * What opens a stack of books, in one plain sentence ("Opens after 1 more
 * level 2 word and the sight words") with the sight words she still has to
 * meet set in the reading face on a line of their own, like the sets above.
 * The level's phrasing is core's unlockPhrase; only the words are drawn here.
 */
function Gate({ unlock }: { unlock?: StepUnlock }) {
  const parts = !unlock ? [] : unlock.kind === 'all' ? unlock.parts : [unlock]
  if (!parts.length) return <>Not yet</>
  const words = parts.flatMap((p) => (p.kind === 'meet' ? p.words : []))
  const rest = parts.filter((p) => p.kind !== 'meet').map((p) => unlockPhrase(p))
  const sentence = [...rest, ...(words.length ? [words.length === 1 ? 'the sight word' : 'the sight words'] : [])].join(' and ')
  return (
    <>
      Opens after {sentence}
      {words.length > 0 && (
        <span className="path-gate-words">
          {words.slice(0, 4).map((w) => <span key={w} className="path-word">{w}</span>)}
          {words.length > 4 && <span>and {words.length - 4} more</span>}
        </span>
      )}
    </>
  )
}

/**
 * A book still ahead, opened for the parent: its cover, the whole story as
 * text, and what opens it for her. She plays it once it opens; this is for
 * the grown-up deciding what is coming, or reading it to her.
 */
function BookPeek({ step: s, onClose }: { step: StepStanding; onClose: () => void }) {
  const pages = (s.step.config.pages ?? []) as Array<{ text: string }>
  return (
    <section className="path-peek reveal" aria-label={`${s.step.title}, a look inside`}>
      <Cover word={s.step.cover} size={64} />
      <div className="path-peek-body">
        <h4 className="path-peek-title">{s.step.title}</h4>
        <p className="path-peek-meta"><LockIcon /> Level {Number(s.step.config.level)} · {s.unlockHint ?? unlockPhrase(s.unlock)}</p>
        <ol className="path-peek-pages">
          {pages.map((p, i) => <li key={i}>{p.text}</li>)}
        </ol>
      </div>
      <button className="btn btn-quiet btn-sm path-peek-close" onClick={onClose}>Close</button>
    </section>
  )
}

/** A step's cover: its clay picture when the word bank has one, else its emoji. */
export function Cover({ word, size }: { word?: string; size: number }) {
  const src = pictureSrc(word)
  return (
    <span className="path-cover" style={{ width: size, height: size }} aria-hidden="true">
      {src ? <img src={src} alt="" /> : <span className="path-cover-emoji" style={{ fontSize: size * 0.52 }}>{word ?? '📖'}</span>}
    </span>
  )
}

const S = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.1,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true,
}
const PlayIcon = () => <svg {...S} width="12" height="12" fill="currentColor" strokeWidth={1.4}><path d="M8 5.5v13l10.5-6.5z" /></svg>
const LockIcon = () => (
  <svg {...S} width="12" height="12" strokeWidth={2}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
)
const ArrowIcon = () => <svg {...S} width="16" height="16"><path d="M9 5l7 7-7 7" /></svg>
const CheckIcon = () => <svg {...S} width="12" height="12"><path d="M5 12.5l4.2 4L19 7" /></svg>
